import { useEffect, useState } from "react";
import _ from "lodash";
import { apiGet } from "../../utils/api";
import { DEFAULT_BUFFER_DAYS } from "./rdSummary";

/**
 * 里程碑的提醒門檻（黃燈要提前幾天亮）。
 *
 * 值存在後端 system_setting 的 goal.buffer_days，由「設定 → 偏好設定」維護。
 * 讀取端點 GET /Setting/Goal 只掛 [Authorize]、沒有掛 setting 權限 ——
 * 時程頁要靠這個數字畫黃燈，而 RD 主管沒有 setting 權限，掛了的話這幾頁
 * 會 403、門檻永遠回不到畫面上。
 *
 * 為什麼做成一支共用 hook，而不是每一頁各自 apiGet：
 *   黃／紅的判定只有一份（rdSummary.buildScheduleView），門檻是它的輸入值，
 *   而時程軸、新增里程碑、排工項三頁都要餵同一個值。各寫一份的話遲早有一頁忘了接，
 *   變成時程頁說「快貼線」、排工項頁說「還有緩衝」—— 兩邊就都沒人信。
 *
 * 走 apiGet 不進 redux：這是一個只讀、全站共用的單一數字，
 * 為它加 service/saga/reducer 只是多三個檔案要維護（同 Preferences 的編碼規則）。
 */
export default function useGoalBufferDays() {
  const [bufferDays, setBufferDays] = useState(DEFAULT_BUFFER_DAYS);
  /*
   * 「讀到設定值」與「退回預設值」要分得出來。
   * 畫面上會把這個數字寫進句子裡，讀不到卻講得像有人設定過，
   * 就是編一個使用者驗證不了的說法（同這一頁其他缺口的處理方式）。
   */
  const [loaded, setLoaded] = useState(false);
  /*
   * 「還在讀」是第三種狀態，不能跟「讀不到」共用 loaded === false。
   *
   * 只有兩種狀態時，第一個 render 一定是 false —— 而 GoalNew 沒有任何 early return，
   * 表單當場就畫出來，於是每一次進「新增里程碑」都會先出現一句
   * 「現在讀不到設定值，底下顯示的是預設值」，配上一個還沒換成真值的天數。
   * 請求其實好好的，只是還沒回來；網路慢時那句假情報會停在畫面上好幾秒。
   */
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiGet("/Setting/Goal")
      .then((res) => {
        if (!alive) return;
        const n = Number(_.get(res, "buffer_days"));
        /* 形狀不對就當作沒讀到：拿 NaN 去比較的話每個里程碑都會算不出黃燈，
           而且畫面不會有任何跡象說明為什麼 */
        if (!Number.isFinite(n) || n <= 0) return;
        setBufferDays(n);
        setLoaded(true);
      })
      .catch(() => {
        /* 讀不到不是錯誤情境（離線、端點還沒佈上去）：門檻只決定黃燈提早幾天亮，
           退回 DEFAULT_BUFFER_DAYS 之後整頁仍然是對的，不必打斷使用者 */
      })
      /* 成功、形狀不對、失敗三條路都要關掉「讀取中」，否則畫面會一直停在讀取中的句子 */
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return { bufferDays, loaded, loading };
}
